/* ============================================================
   TASK-1756 — ILS custom cart icon (basket-fill + add animation)
   TASK-2068/2069 — badge stale after remove/add + close (fixed).
   TASK-2070 — owner report: TOTAL AMOUNT and the RED FILL LEVEL still
   went stale on close (badge count was fixed, total/fill were not).
   Root cause: a remove/add click can leave an in-flight /cart/change or
   /cart/add AJAX request when the drawer closes; fetching /cart.js
   immediately in the closed handler could race AHEAD of that request and
   read the pre-change cart. Fix: the closed handler now waits 350ms
   (enough for Dawn's own AJAX to settle) before firing the authoritative
   /cart.js re-fetch, so total + fill level are always read fresh.
   ============================================================ */
(function () {
  'use strict';

  var MAX     = 30;
  var START   = 16.83;
  var END     = 9.89;
  var EMPTY_Y = 19.6;
  var SPLASH  = 9.7;

  var lastKnownCount = null;
  var displayedCount = null;

  var pendingAddCount = null;
  var flyLanded = false;
  var bumpFallbackTimer = null;
  var flyFallbackTimer = null;

  var badgeFrozen = false;
  var frozenCount = null;

  var lastKnownTotalCents = null;

  var pendingAddTotalCents = null;

  var totalFrozen = false;
  var frozenTotalCents = null;

  var lastSilentRefetchAt = 0;

  /* TASK-2125 — expose a small handshake so custom-cart-drawer.js's
     confetti trigger can wait for the header cart-icon's badge/total
     to visually update after an add. `pending` flips to true the moment
     we know a user add is in flight (either via click detection or via
     PUB_SUB cartUpdate showing item_count > prev), and false at the
     exact moment `playAddSequence` swaps the badge text and unfreezes
     the total. `ils:cart:number-updated` fires on the same beat. */
  window.ILS = window.ILS || {};
  window.ILS._cartAddAnim = { pending: false };
  function markAddAnimPending() {
    window.ILS._cartAddAnim.pending = true;
  }
  function markAddAnimResolved() {
    if (window.ILS._cartAddAnim.pending === false) return;
    window.ILS._cartAddAnim.pending = false;
    try { document.dispatchEvent(new CustomEvent('ils:cart:number-updated')); } catch (e) {}
  }

  function levelY(c) {
    if (c <= 0) return EMPTY_Y;
    if (c >= MAX) return END;
    var t = (c - 1) / (MAX - 1);
    return START - (START - END) * t;
  }

  function formatMoneyCents(cents) {
    /* TASK-5795 — snippets/custom-cart-icon.liquid renders this very figure
       server-side as {{ cart.total_price | money }}, i.e. "150,00 kr" on
       /en-se/. readInitial() below then replaced it with the legacy euro body's
       "€150,00", so the header badge contradicted the subtotal three lines
       lower on the same page. ILSMoney prints it the way Shopify just did; null
       falls through to the legacy body, so EUR markets are unchanged. */
    if (window.ILSMoney && window.ILSMoney.on()) {
      var _p5795 = window.ILSMoney.fromCents(parseInt(cents, 10));
      if (_p5795 !== null) return _p5795;
    }
    var n = parseInt(cents, 10);
    if (!isFinite(n)) n = 0;
    var sign = n < 0 ? '-' : '';
    var abs = Math.abs(n | 0);
    var euros = Math.floor(abs / 100);
    var c = String(abs % 100);
    while (c.length < 2) c = '0' + c;
    var eFormatted = String(euros).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    /* TASK-5946 — display only: a FULLY zero fraction prints as no fraction. */
    return sign + '€' + eFormatted + (c === '00' ? '' : ',' + c);
  }

  function updateTotalText(cents) {
    var el = document.querySelector('[data-ils-cart-total]');
    if (!el) return;
    el.textContent = formatMoneyCents(cents);
  }

  function applyTotalEmptyClass() {
    var el = document.querySelector('[data-ils-cart-total]');
    if (!el) return;
    var emptyByTotal = (lastKnownTotalCents !== null) && lastKnownTotalCents <= 0;
    var emptyByCount = (lastKnownCount !== null) && lastKnownCount <= 0;
    if (emptyByTotal || emptyByCount) el.classList.add('is-empty');
    else el.classList.remove('is-empty');
  }

  function handleTotal(cents) {
    var n = parseInt(cents, 10);
    if (!isFinite(n)) return;
    lastKnownTotalCents = n;
    updateTotalText(n);
    applyTotalEmptyClass();
  }

  function fetchTotalFromCart() {
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        if (cart && typeof cart.total_price === 'number') handleTotal(cart.total_price);
      })
      .catch(function () {});
  }

  function getIcon() {
    return document.querySelector('#cart-icon-bubble .ils-cart')
        || document.querySelector('.ils-cart');
  }

  function setFillEl(fill, y, animated) {
    if (!fill) return;
    if (animated === false) {
      fill.style.transition = 'none';
    }
    fill.setAttribute('y', y.toFixed(2));
    fill.setAttribute('height', (24 - y).toFixed(2));
    if (animated === false) {
      // eslint-disable-next-line no-unused-expressions
      fill.getBoundingClientRect();
      fill.style.transition = '';
    }
  }

  function syncFromCount(count, opts) {
    var icon = getIcon();
    if (!icon) return;
    var fill = icon.querySelector('.fill-rect');
    var badge = icon.querySelector('.ils-cart__count, [data-ils-cart-count]');
    var shownCount = (badgeFrozen && frozenCount !== null) ? frozenCount : count;
    if (badge) {
      badge.textContent = String(shownCount);
      badge.classList.toggle('is-shown', shownCount > 0);
      if (!badgeFrozen) displayedCount = count;
    }
    setFillEl(fill, levelY(shownCount), opts && opts.animated);
  }

  function playAddSequence(newCount) {
    var icon = getIcon();
    if (!icon) return;
    var fill = icon.querySelector('.fill-rect');
    var badge = icon.querySelector('.ils-cart__count, [data-ils-cart-count]');
    var drop = icon.querySelector('.ils-cart__drop');

    var landed = false;
    function onLanded() {
      if (landed) return;
      landed = true;

      icon.classList.remove('kick');
      void icon.offsetWidth;
      icon.classList.add('kick');

      if (fill) {
        fill.style.transition = 'y .45s cubic-bezier(.3,.9,.4,1), height .45s cubic-bezier(.3,.9,.4,1)';
        setFillEl(fill, SPLASH, true);
        setTimeout(function () {
          fill.style.transition = 'y 2.1s cubic-bezier(.33,0,.2,1), height 2.1s cubic-bezier(.33,0,.2,1)';
          setFillEl(fill, levelY(newCount), true);
        }, 480);
      }

      setTimeout(function () {
        if (badge) {
          badge.classList.add('is-shown');
          badge.classList.remove('bump');
          void badge.offsetWidth;
          badge.classList.add('bump');
        }
        setTimeout(function () {
          badgeFrozen = false;
          frozenCount = null;
          if (badge) {
            badge.textContent = String(newCount);
            displayedCount = newCount;
          }
          totalFrozen = false;
          frozenTotalCents = null;
          if (pendingAddTotalCents !== null) {
            handleTotal(pendingAddTotalCents);
            pendingAddTotalCents = null;
          }
          /* TASK-2136 — DO NOT resolve markAddAnimResolved here. The pending
             flag must stay TRUE until ils:add:bump-done fires, otherwise
             fireConfettiWhenReady in custom-cart-drawer.js sees pending=false
             during the 200 ms window before bump-done and fires confetti
             immediately, which the owner perceives as "confetti before the
             plate". Resolution moved into the 380 ms setTimeout below,
             immediately before the bump-done dispatch. */
        }, 180);
        setTimeout(function () {
          /* TASK-2136 — resolve pending flag here, then dispatch bump-done
             in the same tick, so any confetti trigger waiting on either
             signal fires synchronously with the toast (whose showToastCenter
             listener also runs off bump-done). */
          markAddAnimResolved();
          try { document.dispatchEvent(new CustomEvent('ils:add:bump-done')); } catch (e) {}
        }, 380);
      }, 450);
    }

    if (drop) {
      drop.classList.remove('go');
      void drop.offsetWidth;
      drop.addEventListener('animationend', onLanded, { once: true });
      drop.classList.add('go');
      setTimeout(onLanded, 600);
    } else {
      onLanded();
    }
  }

  function handleCount(newCount) {
    if (typeof newCount !== 'number' || isNaN(newCount)) return;
    var prev = lastKnownCount;
    lastKnownCount = newCount;
    applyTotalEmptyClass();
    if (prev === null) {
      syncFromCount(newCount, { animated: false });
      return;
    }
    if (newCount > prev) {
      badgeFrozen = true;
      frozenCount = (displayedCount !== null) ? displayedCount : prev;
      if (!totalFrozen && lastKnownTotalCents !== null) {
        totalFrozen = true;
        frozenTotalCents = lastKnownTotalCents;
      }
      pendingAddCount = newCount;
      markAddAnimPending();
      if (flyFallbackTimer) clearTimeout(flyFallbackTimer);
      flyFallbackTimer = setTimeout(function () {
        flyLanded = true;
        tryRunBump();
      }, 1200);
      tryRunBump();
    } else {
      syncFromCount(newCount, { animated: true });
    }
  }

  function tryRunBump() {
    if (pendingAddCount == null || !flyLanded) {
      if (flyLanded && pendingAddCount == null) {
        if (bumpFallbackTimer) clearTimeout(bumpFallbackTimer);
        bumpFallbackTimer = setTimeout(function () {
          if (pendingAddCount == null) {
            flyLanded = false;
            badgeFrozen = false;
            frozenCount = null;
            totalFrozen = false;
            frozenTotalCents = null;
            if (lastKnownCount !== null) syncFromCount(lastKnownCount, { animated: true });
            if (pendingAddTotalCents !== null) {
              handleTotal(pendingAddTotalCents);
              pendingAddTotalCents = null;
            } else if (lastKnownTotalCents !== null) {
              updateTotalText(lastKnownTotalCents);
              applyTotalEmptyClass();
            }
            markAddAnimResolved();
            try { document.dispatchEvent(new CustomEvent('ils:add:bump-done')); } catch (e) {}
          }
        }, 1200);
      }
      return;
    }
    if (flyFallbackTimer) { clearTimeout(flyFallbackTimer); flyFallbackTimer = null; }
    if (bumpFallbackTimer) { clearTimeout(bumpFallbackTimer); bumpFallbackTimer = null; }
    var n = pendingAddCount;
    pendingAddCount = null;
    flyLanded = false;
    playAddSequence(n);
  }

  function readInitial() {
    var icon = getIcon();
    if (!icon) return;
    var badge = icon.querySelector('.ils-cart__count, [data-ils-cart-count]');
    var n = 0;
    if (badge && badge.textContent) {
      var parsed = parseInt(badge.textContent.replace(/\D+/g, ''), 10);
      if (!isNaN(parsed)) n = parsed;
    }
    handleCount(n);

    var totalEl = document.querySelector('[data-ils-cart-total]');
    if (totalEl) {
      var cents = parseInt(totalEl.getAttribute('data-cents'), 10);
      if (isFinite(cents)) handleTotal(cents);
      else fetchTotalFromCart();
    }
  }

  function wireDawnSub() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
        var cartData = event && event.cartData;
        var haveCount = cartData && typeof cartData.item_count === 'number';
        var haveTotal = cartData && typeof cartData.total_price === 'number';
        var isAdd = haveCount && lastKnownCount !== null && cartData.item_count > lastKnownCount;
        if (haveCount) handleCount(cartData.item_count);
        if (haveTotal) {
          if (isAdd) {
            pendingAddTotalCents = cartData.total_price;
            if (!totalFrozen && lastKnownTotalCents !== null) {
              totalFrozen = true;
              frozenTotalCents = lastKnownTotalCents;
            }
          } else {
            handleTotal(cartData.total_price);
            setTimeout(function () {
              updateTotalText(cartData.total_price);
              applyTotalEmptyClass();
            }, 0);
          }
        }
        if (!haveCount || !haveTotal) {
          fetch('/cart.js', { headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              if (!cart) return;
              if (!haveCount && typeof cart.item_count === 'number') handleCount(cart.item_count);
              if (!haveTotal && typeof cart.total_price === 'number') {
                if (isAdd) {
                  pendingAddTotalCents = cart.total_price;
                  if (!totalFrozen && lastKnownTotalCents !== null) {
                    totalFrozen = true;
                    frozenTotalCents = lastKnownTotalCents;
                  }
                } else {
                  handleTotal(cart.total_price);
                }
              }
            })
            .catch(function () {});
        }
      });
    }
  }

  function observeMorph() {
    var bubble = document.querySelector('#cart-icon-bubble') || document.body;
    if (!bubble) return;
    var applying = false;
    var mo = new MutationObserver(function () {
      if (applying) return;
      applying = true;
      Promise.resolve().then(function () {
        try {
          var c = (badgeFrozen && frozenCount !== null)
            ? frozenCount
            : ((displayedCount !== null) ? displayedCount : lastKnownCount);
          var ic = getIcon();
          if (ic && c !== null) {
            var fill = ic.querySelector('.fill-rect');
            if (fill) setFillEl(fill, levelY(c), false);
            var b = ic.querySelector('.ils-cart__count, [data-ils-cart-count]');
            if (b && b.textContent !== String(c)) {
              b.textContent = String(c);
            }
          }
          var totalEl = document.querySelector('[data-ils-cart-total]');
          if (totalEl) {
            var displayCents = (totalFrozen && frozenTotalCents !== null)
              ? frozenTotalCents
              : lastKnownTotalCents;
            if (displayCents !== null) {
              var desired = formatMoneyCents(displayCents);
              if (totalEl.textContent !== desired) totalEl.textContent = desired;
            }
            applyTotalEmptyClass();
          }
        } catch (e) {}
        applying = false;
      });
    });
    mo.observe(bubble, { childList: true, subtree: true, characterData: true });
  }

  function silentSyncFromCart(cart) {
    if (!cart) return;
    if (typeof cart.item_count === 'number') {
      lastKnownCount = cart.item_count;
      if (!badgeFrozen) displayedCount = cart.item_count;
      syncFromCount(cart.item_count, { animated: false });
    }
    if (typeof cart.total_price === 'number') {
      handleTotal(cart.total_price);
    } else {
      applyTotalEmptyClass();
    }
  }

  function refetchCartSilent() {
    if (badgeFrozen) return;
    var now = Date.now();
    if (now - lastSilentRefetchAt < 1000) return;
    lastSilentRefetchAt = now;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(silentSyncFromCart)
      .catch(function () {});
  }

  function init() {
    readInitial();
    wireDawnSub();
    observeMorph();
    window.addEventListener('pageshow', function () { refetchCartSilent(); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') refetchCartSilent();
    });
    document.addEventListener('ils:add:fly-landed', function () {
      flyLanded = true;
      tryRunBump();
    });
    // TASK-2068/2069/2070 — force an authoritative re-fetch after the drawer
    // closes via the X. Clear freeze state immediately (so a mid-animation
    // close can't block it), then wait 350ms before fetching /cart.js so any
    // in-flight remove/add AJAX request has time to land server-side first —
    // this is what fixes the total amount + red fill level staying stale.
    document.addEventListener('ils:cart-drawer:closed', function () {
      badgeFrozen = false;
      frozenCount = null;
      totalFrozen = false;
      frozenTotalCents = null;
      setTimeout(function () {
        lastSilentRefetchAt = 0;
        refetchCartSilent();
      }, 350);
    });
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var addBtn = t.closest(
        'product-form button[type="submit"], form[action*="/cart/add"] button[type="submit"], [data-ils-add], .ils-pcard__add, button[name="add"]'
      );
      if (!addBtn) return;
      if (!badgeFrozen) {
        badgeFrozen = true;
        frozenCount = (displayedCount !== null) ? displayedCount : lastKnownCount;
      }
      if (!totalFrozen && lastKnownTotalCents !== null) {
        totalFrozen = true;
        frozenTotalCents = lastKnownTotalCents;
      }
      /* TASK-2125 — mark the add-animation pending as EARLY as possible
         (on click, before the /cart/add.js round-trip) so any confetti
         trigger firing later in the cartUpdate handler knows to wait. */
      markAddAnimPending();
    }, true);
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
